import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import stripeApiService from '../../../services/stripe-subscription.service';
import {
  IconCreditCard,
  IconTrash,
  IconEdit,
  IconCheck,
  IconX,
  IconPlus,
} from '@tabler/icons-react';
import PropTypes from 'prop-types';
import DeletePaymentMethodModal from './DeletePaymentMethodModal';
import './SavedCardsModal.css';

const SavedCardsModal = ({
  isOpen,
  onClose,
  stripeCustomerId,
  savedPaymentMethods,
  defaultPaymentMethodId,
  onPaymentMethodChanged,
  onDeletePaymentMethod,
  onAddNewCard,
  onEditBillingAddress,
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultMethodId, setDefaultMethodId] = useState(null);
  const [selectedMethodId, setSelectedMethodId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const [isLoadingMethods, setIsLoadingMethods] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);

  // Fetch payment methods when modal opens
  const fetchPaymentMethods = async () => {
    if (!stripeCustomerId) {
      console.warn('SavedCardsModal: No Stripe customer ID provided');
      setPaymentMethods([]);
      setIsLoadingMethods(false);
      return;
    }

    try {
      setIsLoadingMethods(true);
      setFetchError(null);

      console.log('Fetching payment methods for customer:', stripeCustomerId);
      const response = await stripeApiService.getPaymentMethods(stripeCustomerId);
      
      console.log('Payment methods API response:', response);
      
      // Parse response - Stripe API returns:
      // { 
      //   payment_methods: { object: "list", data: [...] }, 
      //   default_payment_method: "pm_xxx" 
      // }
      const methods = response.payment_methods?.data || [];
      const defaultId =
        response.default_payment_method ||
        defaultPaymentMethodId ||
        (methods.length > 0 ? methods[0].id : null);

      console.log('Parsed payment methods:', methods);
      console.log('Default payment method ID:', defaultId);

      setPaymentMethods(Array.isArray(methods) ? methods : []);
      setDefaultMethodId(defaultId);

    } catch (err) {
      console.error('Failed to fetch payment methods:', err);
      setFetchError('Failed to load payment methods');
      
      // Fallback to prop data if API fails
      setPaymentMethods(savedPaymentMethods || []);
      setDefaultMethodId(defaultPaymentMethodId);
      
      toast.error('Could not load payment methods');
    } finally {
      setIsLoadingMethods(false);
    }
  };

  // Initialize payment methods when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('SavedCardsModal opened with stripeCustomerId:', stripeCustomerId);
      fetchPaymentMethods();
      setSelectedMethodId(null);
    }
  }, [isOpen, stripeCustomerId]);

  // Get card brand display name
  const getCardBrandDisplay = (brand) => {
    const brandMap = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
      diners: 'Diners Club',
      jcb: 'JCB',
      unionpay: 'UnionPay',
    };
    return brandMap[brand?.toLowerCase()] || brand?.toUpperCase() || 'Card';
  };

  // Get cardholder name or fallback
  const getCardHolderName = (method) => {
    return method.billing_details?.name || 'No name provided';
  };

  // Format billing address
  const formatBillingAddress = (method) => {
    const address = method.billing_details?.address;
    if (!address) return 'No address provided';

    const parts = [];
    if (address.line1) parts.push(address.line1);
    if (address.line2) parts.push(address.line2);
    
    const cityState = [address.city, address.state].filter(Boolean).join(', ');
    if (cityState) parts.push(cityState);
    
    if (address.postal_code) parts.push(address.postal_code);

    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  };

  // Handle setting a payment method as default
  const handleSetAsDefault = async (methodId) => {
    if (methodId === defaultMethodId) {
      return; // Already default
    }

    try {
      setSettingDefaultId(methodId);
      
      await stripeApiService.updateSubscriptionPaymentMethod(
        stripeCustomerId,
        methodId,
        true, // isExisting = true
      );

      toast.success('Default payment method updated!');
      
      // Update local state immediately
      setDefaultMethodId(methodId);
      
      // Notify parent and refresh
      onPaymentMethodChanged(false);
      
      // Optionally refetch to ensure sync
      await fetchPaymentMethods();
    } catch (err) {
      console.error('Set default error:', err);
      toast.error('Failed to update default payment method');
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Handle payment method deletion - Open confirmation modal
  const handleDeletePaymentMethod = (method) => {
    // Check if trying to delete default method
    if (method.id === defaultMethodId) {
      toast.error('Cannot delete the default payment method. Please set another card as default first.');
      return;
    }

    // Open confirmation modal with method details
    setMethodToDelete(method);
    setDeleteModalOpen(true);
  };

  // Confirm and execute payment method deletion
  const confirmDeletePaymentMethod = async () => {
    if (!methodToDelete) return;

    const methodId = methodToDelete.id;

    try {
      setDeletingId(methodId);
      
      await stripeApiService.deletePaymentMethod(stripeCustomerId, methodId);

      // Update local state
      setPaymentMethods((prevMethods) =>
        prevMethods.filter((method) => method.id !== methodId)
      );

      if (selectedMethodId === methodId) {
        setSelectedMethodId(null);
      }

      toast.success('Payment method deleted successfully');
      onDeletePaymentMethod();
      
      // Close modal and reset state
      setDeleteModalOpen(false);
      setMethodToDelete(null);
      
      // Refetch to ensure sync
      await fetchPaymentMethods();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete payment method');
    } finally {
      setDeletingId(null);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    if (!deletingId) {
      setDeleteModalOpen(false);
      setMethodToDelete(null);
    }
  };

  // Handle edit billing address
  const handleEditAddress = (method) => {
    onEditBillingAddress(method);
  };

  // Get card icon based on brand
  const getCardIcon = (brand) => {
    // You can replace this with actual brand icons if available
    return <IconCreditCard size={24} />;
  };

  // Separate default and other payment methods
  const defaultMethod = paymentMethods.find(
    (method) => method.id === defaultMethodId
  );
  const otherMethods = paymentMethods.filter(
    (method) => method.id !== defaultMethodId
  );

  return (
    <>
      <Modal.Header className="saved-cards-modal-header">
        <div>
          <Modal.Title as="h3">Payment Methods</Modal.Title>
          <p className="modal-subtitle">
            Manage your saved cards and billing information
          </p>
        </div>
        <button className="modal-close-btn" onClick={onClose}>
          <IconX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className="saved-cards-modal-body">
        {/* Loading State */}
        {isLoadingMethods ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading payment methods...</p>
          </div>
        ) : (
          <>
            {/* Add New Card Button */}
            <button className="add-card-button" onClick={onAddNewCard}>
              <IconPlus size={18} />
              Add New Card
            </button>

            {paymentMethods.length === 0 ? (
              /* Empty State */
              <div className="empty-state">
                <IconCreditCard size={48} className="empty-icon" />
                <h4>No Payment Methods</h4>
                <p>
                  {!stripeCustomerId 
                    ? 'Customer account not set up' 
                    : fetchError 
                    ? 'Unable to load payment methods' 
                    : 'Add a payment method to get started'}
                </p>
              </div>
            ) : (
              <>
            {/* Default Payment Method */}
            {defaultMethod && (
              <div className="payment-method-section">
                <h4 className="section-title">Default Payment Method</h4>
                <div className="payment-card default-card">
                  <div className="card-header">
                    <div className="card-brand-info">
                      {getCardIcon(defaultMethod.card.brand)}
                      <div className="card-details">
                        <h5 className="card-name">
                          {getCardBrandDisplay(defaultMethod.card.brand)} •••• {defaultMethod.card.last4}
                        </h5>
                        <p className="card-holder">
                          {getCardHolderName(defaultMethod)}
                        </p>
                      </div>
                    </div>
                    <div className="card-badges">
                      <span className="badge badge-default">
                        <IconCheck size={14} />
                        Default
                      </span>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="card-info-row">
                      <span className="info-label">Expires</span>
                      <span className="info-value">
                        {String(defaultMethod.card.exp_month).padStart(2, '0')}/
                        {String(defaultMethod.card.exp_year).slice(-2)}
                      </span>
                    </div>
                    <div className="card-info-row">
                      <span className="info-label">Billing Address</span>
                      <span className="info-value">
                        {formatBillingAddress(defaultMethod)}
                      </span>
                    </div>
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn-action btn-edit btn-full-width"
                      onClick={() => handleEditAddress(defaultMethod)}
                    >
                      <IconEdit size={16} />
                      Edit Billing Address
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Other Payment Methods */}
            {otherMethods.length > 0 && (
              <div className="payment-method-section">
                <h4 className="section-title">Other Payment Methods</h4>
                <div className="payment-cards-list">
                  {otherMethods.map((method) => (
                    <div
                      key={method.id}
                      className={`payment-card ${
                        selectedMethodId === method.id ? 'selected' : ''
                      }`}
                      onClick={() => setSelectedMethodId(method.id)}
                    >
                      <div className="card-header">
                        <div className="card-brand-info">
                          {getCardIcon(method.card.brand)}
                          <div className="card-details">
                            <h5 className="card-name">
                              {getCardBrandDisplay(method.card.brand)} •••• {method.card.last4}
                            </h5>
                            <p className="card-holder">
                              {getCardHolderName(method)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="card-info-row">
                          <span className="info-label">Expires</span>
                          <span className="info-value">
                            {String(method.card.exp_month).padStart(2, '0')}/
                            {String(method.card.exp_year).slice(-2)}
                          </span>
                        </div>
                        <div className="card-info-row">
                          <span className="info-label">Billing Address</span>
                          <span className="info-value">
                            {formatBillingAddress(method)}
                          </span>
                        </div>
                      </div>

                      <div className="card-actions">
                        <button
                          className="btn-action btn-default btn-full-width"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetAsDefault(method.id);
                          }}
                          disabled={settingDefaultId === method.id}
                        >
                          {settingDefaultId === method.id ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              <span className="ms-2">Setting...</span>
                            </>
                          ) : (
                            <>
                              <IconCheck size={16} />
                              Set as Default
                            </>
                          )}
                        </button>
                        <button
                          className="btn-action btn-edit btn-full-width"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAddress(method);
                          }}
                        >
                          <IconEdit size={16} />
                          Edit Billing Address
                        </button>
                        <button
                          className="btn-action btn-delete btn-full-width"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePaymentMethod(method);
                          }}
                          disabled={deletingId === method.id}
                        >
                          {deletingId === method.id ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              <span className="ms-2">Deleting...</span>
                            </>
                          ) : (
                            <>
                              <IconTrash size={16} />
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        </>
        )}
      </Modal.Body>

      <Modal.Footer className="saved-cards-modal-footer">
        <button className="btn-close-footer" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>

      {/* Delete Payment Method Confirmation Modal */}
      <DeletePaymentMethodModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirmDelete={confirmDeletePaymentMethod}
        paymentMethod={methodToDelete}
        isDeleting={!!deletingId}
        isDefaultMethod={methodToDelete?.id === defaultMethodId}
      />
    </>
  );
};

SavedCardsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stripeCustomerId: PropTypes.string,
  savedPaymentMethods: PropTypes.array,
  defaultPaymentMethodId: PropTypes.string,
  onPaymentMethodChanged: PropTypes.func.isRequired,
  onDeletePaymentMethod: PropTypes.func.isRequired,
  onAddNewCard: PropTypes.func.isRequired,
  onEditBillingAddress: PropTypes.func.isRequired,
};

export default SavedCardsModal;
